import * as React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth, ApiError } from '@/lib/auth';
import { QRCodeSVG } from 'qrcode.react';
import { Button, PasswordInput } from '@/components/ui';
import { useI18n } from '@/lib/i18n';
import logoUrl from '@/assets/suzuki-logo.png';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { login, user, challenge, submitMfaCode, setupTotp, verifyTotpSetup, completeNewPassword, clearChallenge } = useAuth();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  // MFA state
  const [mfaCode, setMfaCode] = React.useState('');
  // MFA setup state
  const [totpSecret, setTotpSecret] = React.useState('');
  const [setupCode, setSetupCode] = React.useState('');
  // New password state
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');

  const from = (location.state as { from?: string })?.from || '/';

  React.useEffect(() => {
    if (user) {
      // ponytail: customers land on /portal, agents on /dashboard.
      // If `from` is just "/" (no saved location), pick based on role.
      const dest = from === '/' ? (user.role === 'customer' ? '/portal' : '/dashboard') : from;
      navigate(dest, { replace: true });
    }
  }, [user, navigate, from]);

  // Initiate TOTP setup when MFA_SETUP challenge appears
  React.useEffect(() => {
    if (challenge?.type === 'MFA_SETUP' && !totpSecret) {
      setupTotp().then(setTotpSecret).catch((err) => setError(getErrorMessage(err, t)));
    }
  }, [challenge, totpSecret, setupTotp, t]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
    } catch (err) {
      setError(getErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await submitMfaCode(mfaCode);
    } catch (err) {
      setError(getErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }

  async function handleTotpVerify(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await verifyTotpSetup(setupCode);
    } catch (err) {
      setError(getErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }
    if (newPassword.length < 12) {
      setError(t('auth.passwordMinimum', { count: 12 }));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await completeNewPassword(newPassword);
    } catch (err) {
      setError(getErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }

  // ─── Render challenge-specific views ───────────────────────────────────────

  if (challenge?.type === 'MFA_REQUIRED') {
    return (
      <LoginShell>
        <h1 className="text-2xl font-bold text-ink">{t('auth.twoFactor')}</h1>
        <p className="mt-2 text-sm text-ink-muted">{t('auth.enterAuthenticatorCode')}</p>
        <form onSubmit={handleMfaSubmit} className="mt-6 space-y-4" noValidate>
          {error && <ErrorBanner message={error} />}
          <div>
            <label htmlFor="mfa-code" className="block text-sm font-medium text-ink-muted">
              {t('auth.verificationCode')}
            </label>
            <input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              required
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="mt-1 block w-full rounded-lg border border-line-strong px-3 py-2 text-center text-2xl tracking-widest shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
              placeholder="000000"
            />
          </div>
          <Button type="submit" disabled={loading || mfaCode.length !== 6} className="w-full">
            {loading ? t('auth.verifying') : t('auth.verify')}
          </Button>
          <button type="button" onClick={() => { clearChallenge(); setMfaCode(''); }} className="w-full text-sm text-ink-subtle transition-colors hover:text-ink-muted">
            {t('common.backToLogin')}
          </button>
        </form>
      </LoginShell>
    );
  }

  if (challenge?.type === 'MFA_SETUP') {
    return (
      <LoginShell>
        <h1 className="text-2xl font-bold text-ink">{t('auth.setupAuthenticator')}</h1>
        <p className="mt-2 text-sm text-ink-muted">{t('auth.scanAuthenticator')}</p>
        {totpSecret ? (
          <div className="mt-4 space-y-4">
            <div className="flex justify-center rounded-xl border border-line bg-surface p-4">
              <QRCodeSVG
                aria-label={t('auth.authenticatorSetupQr')}
                includeMargin
                level="M"
                size={192}
                value={`otpauth://totp/${encodeURIComponent(`SuzukiDesk:${email}`)}?secret=${encodeURIComponent(totpSecret)}&issuer=SuzukiDesk&algorithm=SHA1&digits=6&period=30`}
              />
            </div>
            <form onSubmit={handleTotpVerify} className="space-y-4" noValidate>
              {error && <ErrorBanner message={error} />}
              <div>
                <label htmlFor="setup-code" className="block text-sm font-medium text-ink-muted">
                  {t('auth.verificationCodeFromAuthenticator')}
                </label>
                <input
                  id="setup-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={setupCode}
                  onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="mt-1 block w-full rounded-lg border border-line-strong px-3 py-2 text-center text-2xl tracking-widest shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                  placeholder="000000"
                />
              </div>
              <Button type="submit" disabled={loading || setupCode.length !== 6} className="w-full">
                {loading ? t('auth.verifying') : t('auth.verifyAndComplete')}
              </Button>
            </form>
          </div>
        ) : (
          <div className="mt-4 text-center text-sm text-ink-subtle">{t('auth.loadingSetup')}</div>
        )}
      </LoginShell>
    );
  }

  if (challenge?.type === 'NEW_PASSWORD_REQUIRED') {
    return (
      <LoginShell>
        <h1 className="text-2xl font-bold text-ink">{t('auth.setNewPassword')}</h1>
        <p className="mt-2 text-sm text-ink-muted">
          {t('auth.temporaryPasswordExpired')}
        </p>
        <form onSubmit={handleNewPassword} className="mt-6 space-y-4" noValidate>
          {error && <ErrorBanner message={error} />}
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-ink-muted">
              {t('auth.newPassword')}
            </label>
            <div className="mt-1">
              <PasswordInput
                id="new-password"
                required
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="block w-full rounded-lg border border-line-strong px-3 py-2 shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
              />
            </div>
            <p className="mt-1 text-xs text-ink-subtle">
              {t('auth.passwordRequirements')}
            </p>
          </div>
          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-ink-muted">
              {t('auth.confirmPassword')}
            </label>
            <div className="mt-1">
              <PasswordInput
                id="confirm-password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="block w-full rounded-lg border border-line-strong px-3 py-2 shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
              />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full">
            {loading ? t('auth.settingPassword') : t('auth.setPassword')}
          </Button>
        </form>
      </LoginShell>
    );
  }

  // ─── Default: email/password login form ────────────────────────────────────

  return (
    <LoginShell>
      <div className="text-center">
        <img src={logoUrl} alt="Suzuki" className="mx-auto mb-4 h-12" />
        <h1 className="text-2xl font-bold text-ink">{t('auth.signInTitle')}</h1>
        <p className="mt-2 text-sm text-ink-muted">{t('auth.signInSubtitle')}</p>
      </div>

      <form onSubmit={handleLogin} className="mt-8 space-y-6" noValidate>
        {error && <ErrorBanner message={error} />}

        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink-muted">
              {t('auth.emailAddress')}
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-line-strong px-3 py-2 shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
              placeholder="you@suzuki-digital-test.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-ink-muted">
              {t('auth.password')}
            </label>
            <div className="mt-1">
              <PasswordInput
                id="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full rounded-lg border border-line-strong px-3 py-2 shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
              />
            </div>
          </div>
        </div>

        <Button type="submit" disabled={loading} className="w-full">
          {loading ? t('auth.signingIn') : t('auth.signIn')}
        </Button>

        <div className="text-center">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 hover:underline"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>
      </form>
    </LoginShell>
  );
}

// ─── Shared components ───────────────────────────────────────────────────────

function LoginShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-surface-page">
      <div className="h-1.5 bg-brand-600" aria-hidden="true" />
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8 rounded-xl border border-line bg-surface p-8 shadow-raised">
          {children}
        </div>
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-accent-100 bg-accent-50 p-3 text-sm text-accent-700" role="alert">
      {message}
    </div>
  );
}

function getErrorMessage(err: unknown, t: ReturnType<typeof useI18n>['t']): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return t('auth.tooManyAttempts');
    return err.message || t('auth.invalidCredentials');
  }
  if (err instanceof Error) return err.message;
  return t('common.unexpectedError');
}
